import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { ArrowRight, Users, Target, MessageCircle, Shield, Layers, TrendingUp } from 'lucide-react';

const engagementPathways = [
  {
    id: 1,
    icon: Users,
    title: "Organisation-wide Diagnostics & 360° Programmes",
    when: "They need a clear, evidence-based view of leadership capability, behaviour, or culture across functions, levels, or cohorts.",
    enables: [
      "Consistent insight across the organisation",
      "Identification of leadership strengths, risks, and development priorities",
      "A credible foundation for development or intervention"
    ],
    starts: "A scoped diagnostic or 360° programme through Impact AS Plus™."
  },
  {
    id: 2,
    icon: TrendingUp,
    title: "Leadership & Succession Intelligence",
    when: "They need greater confidence in leadership readiness, succession decisions, or the continuity of critical roles.",
    enables: [
      "Clear visibility of leadership pipelines and bench strength",
      "Evidence-based succession and promotion decisions",
      "Early identification of capability and behavioural risk"
    ],
    starts: "Targeted leadership and succession diagnostics, followed by focused development and governance support."
  },
  {
    id: 3,
    icon: Target,
    title: "Graduate Recruitment & Assessment Centres",
    when: "They are designing and delivering robust selection processes for graduate intakes and early-career talent.",
    enables: [
      "Evidence-based selection at scale",
      "Insight into role fit, reasoning, and future potential",
      "Stronger long-term leadership pipelines"
    ],
    starts: "Graduate recruitment or assessment centres aligned to organisational strategy."
  },
  {
    id: 4,
    icon: MessageCircle,
    title: "Targeted Coaching & Behaviour Change",
    when: "Specific leaders, teams, or transitions require behaviour change, reflection, or support.",
    enables: [
      "Deeper self-awareness and behavioural shift",
      "Alignment between leadership intent and impact",
      "Support during high-pressure or high-visibility moments"
    ],
    starts: "Insight-led individual or team coaching, anchored in diagnostic findings."
  },
  {
    id: 5,
    icon: Shield,
    title: "Culture, Ethics, and Organisational Alignment",
    when: "Culture, ethics, conduct, or decision-making norms are affecting performance, trust, or regulatory confidence.",
    enables: [
      "Visibility of cultural patterns and risk areas",
      "Alignment between stated values and lived behaviour",
      "Practical actions to strengthen tone and accountability"
    ],
    starts: "A targeted culture or behavioural diagnostic involving senior leadership."
  },
  {
    id: 6,
    icon: Layers,
    title: "Focused Pilots and Phased Rollouts",
    when: "They want to test an approach before scaling, or introduce leadership diagnostics gradually.",
    enables: [
      "Lower-risk entry",
      "Learning before commitment",
      "Flexibility to adapt scope over time"
    ],
    starts: "A pilot programme with a defined population, followed by review and scale-up."
  }
];

const EngagementPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="How We Work | Leadership Advisory Approach | RESILIGENT"
        description="RESILIGENT's advisory-led approach to leadership development. We advise on the right intervention, at the right time, at the right scale. Insight-led, proportionate, designed for confident decision-making."
        keywords="leadership advisory Nigeria, executive coaching approach Lagos, organisational development methodology Africa, leadership consulting process"
        canonical="/engagement"
      />
      <Header />
      
      <main>
        {/* ═══════════════════════════════════════════════════════════════
            1) HERO - Advisory-Led Opening Stance
        ═══════════════════════════════════════════════════════════════ */}
        <section 
          className="relative min-h-[70vh] lg:min-h-[75vh] flex items-center bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(10, 10, 10, 0.95) 0%, rgba(10, 10, 10, 0.8) 40%, rgba(10, 10, 10, 0.4) 100%), url('/opt/photo/ext/u1573167710701-35950a41e251.webp')`
          }}
        >
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 pt-24">
            <div className="max-w-3xl">
              <h1 className="text-white font-semibold text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight mb-10">
                How We Work
              </h1>
              
              {/* Intro Line - Visually prominent */}
              <p className="text-white text-xl lg:text-2xl font-medium leading-relaxed mb-8">
                We do not sell programmes. We advise on the right intervention, and implement at the right time, at the right scale.
              </p>
              
              {/* Supporting paragraph */}
              <p className="text-slate-300 text-base lg:text-lg leading-relaxed max-w-2xl">
                Our work is always insight-led, proportionate, and designed to support confident decision-making.
              </p>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════
            2) FLEXIBLE ENGAGEMENT SECTION
        ═══════════════════════════════════════════════════════════════ */}
        <section className="bg-gradient-to-br from-slate-50 via-white to-blue-50/30 py-20 lg:py-28 relative overflow-hidden">
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#2693d0]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#2693d0]/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
          
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Main Content */}
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-px bg-[#2693d0]" />
                  <p className="text-[#2693d0] text-sm font-medium tracking-wide uppercase">
                    Flexible engagement
                  </p>
                </div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  There Is No Single "Right" Starting Point
                </h2>
                <div className="space-y-5">
                  <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                    Leadership and organisational challenges rarely present themselves neatly. Some organisations need clarity at scale. Others need to address specific leadership risks, succession questions, or behavioural dynamics.
                  </p>
                  <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                    We therefore design engagements around how organisations actually buy and decide, starting small where appropriate, and scaling once priorities are clear.
                  </p>
                </div>
              </div>
              
              {/* Right Visual Card */}
              <div>
                <div className="bg-white rounded-2xl p-8 border-2 border-slate-200 shadow-xl shadow-slate-200/50 transition-all duration-300 hover:-translate-y-2 hover:scale-[1.02] hover:shadow-2xl hover:border-[#2693d0]/50 animate-float">
                  <div className="flex items-start gap-4 mb-6 pb-6 border-b border-slate-100">
                    <div className="w-12 h-12 rounded-xl bg-[#2693d0]/10 flex items-center justify-center flex-shrink-0">
                      <Target className="w-6 h-6 text-[#2693d0]" />
                    </div>
                    <div>
                      <h3 className="text-slate-800 font-semibold text-lg mb-1">Start where you are</h3>
                      <p className="text-slate-500 text-sm">Not where others expect you to be</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#2693d0]" />
                      <span className="text-slate-600 text-sm">Pilot programmes for lower-risk entry</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#2693d0]" />
                      <span className="text-slate-600 text-sm">Focused diagnostics for specific challenges</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#2693d0]" />
                      <span className="text-slate-600 text-sm">Scale as clarity and value emerge</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════
            3) COMMON ENGAGEMENT PATHWAYS - Blue Background
        ═══════════════════════════════════════════════════════════════ */}
        <section className="relative bg-[#e8f4fa] py-20 lg:py-28 overflow-hidden">
          {/* Decorative Circles */}
          <div className="absolute top-20 left-10 w-64 h-64 border border-[#2693d0]/20 rounded-full" />
          <div className="absolute top-40 left-20 w-48 h-48 border border-[#2693d0]/15 rounded-full" />
          <div className="absolute bottom-20 right-10 w-80 h-80 border border-[#2693d0]/20 rounded-full" />
          <div className="absolute bottom-40 right-32 w-56 h-56 border border-[#2693d0]/15 rounded-full" />
          <div className="absolute top-1/2 left-1/3 w-40 h-40 border border-[#2693d0]/10 rounded-full -translate-y-1/2" />
          
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 relative z-10">
            <div className="mb-12 lg:mb-16">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-4">
                Common Engagement Pathways
              </h2>
              <p className="text-slate-500 text-base lg:text-lg max-w-3xl">
                Below are the most common ways organisations engage RESILIGENT. These are not products or packages, but structured entry points shaped around real organisational needs.
              </p>
            </div>

            {/* Pathways Grid */}
            <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
              {engagementPathways.map((pathway, index) => {
                const Icon = pathway.icon;
                const floatClass = `animate-float-${(index % 6) + 1}`;
                return (
                  <div 
                    key={pathway.id} 
                    className={`bg-white border-2 border-slate-200 rounded-xl p-6 lg:p-8 transition-all duration-300 hover:border-[#2693d0]/50 hover:shadow-xl hover:-translate-y-1 hover:scale-[1.01] ${floatClass}`}
                  >
                    {/* Header */}
                    <div className="flex items-start gap-4 mb-5">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#2693d0]/10 rounded-lg flex-shrink-0">
                        <Icon className="w-5 h-5 text-[#2693d0]" />
                      </div>
                      <h3 className="text-slate-800 font-semibold text-lg lg:text-xl leading-tight">
                        {pathway.title}
                      </h3>
                    </div>
                    
                    {/* When */}
                    <p className="text-slate-600 text-sm lg:text-base leading-relaxed mb-5">
                      <span className="text-slate-800 font-medium">Organisations typically engage us when </span>
                      {pathway.when.charAt(0).toLowerCase() + pathway.when.slice(1)}
                    </p>
                    
                    {/* What it enables */}
                    <div className="mb-5">
                      <p className="text-[#2693d0] text-xs font-medium uppercase tracking-wide mb-2">
                        What this enables
                      </p>
                      <ul className="space-y-1.5">
                        {pathway.enables.map((item, idx) => (
                          <li key={idx} className="text-slate-600 text-sm leading-relaxed flex items-start gap-2">
                            <span className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-1.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {/* How it starts */}
                    <p className="text-slate-500 text-sm">
                      <span className="text-[#2693d0] font-medium uppercase text-xs tracking-wide">How it typically starts: </span>
                      {pathway.starts}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════
            4) WHAT ALL ENGAGEMENTS HAVE IN COMMON
        ═══════════════════════════════════════════════════════════════ */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8">
                What All Engagements Have in Common
              </h2>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-10">
                Regardless of starting point, all RESILIGENT engagements are characterised by:
              </p>
              
              <ul className="space-y-5">
                <li className="flex items-start gap-4">
                  <div className="w-3 h-3 rounded-full bg-[#2693d0] mt-1.5 flex-shrink-0" />
                  <p className="text-slate-700 text-base lg:text-lg">Insight before intervention</p>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-3 h-3 rounded-full bg-[#2693d0] mt-1.5 flex-shrink-0" />
                  <p className="text-slate-700 text-base lg:text-lg">Proportionate, context-aware design</p>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-3 h-3 rounded-full bg-[#2693d0] mt-1.5 flex-shrink-0" />
                  <p className="text-slate-700 text-base lg:text-lg">Integration across individual, team, and organisational levels</p>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-3 h-3 rounded-full bg-[#2693d0] mt-1.5 flex-shrink-0" />
                  <p className="text-slate-700 text-base lg:text-lg">Clear governance and accountability</p>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-3 h-3 rounded-full bg-[#2693d0] mt-1.5 flex-shrink-0" />
                  <p className="text-slate-700 text-base lg:text-lg">The ability to scale as needs evolve</p>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════
            5) POWERED BY IMPACT AS PLUS - Signal Only
        ═══════════════════════════════════════════════════════════════ */}
        <section className="bg-[#f0f5f8] py-16 lg:py-20 border-t border-slate-200">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-xl sm:text-2xl lg:text-3xl mb-6">
                Powered by Impact AS Plus™
              </h2>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                All engagements are supported by Impact AS Plus™, RESILIGENT's leadership intelligence architecture, enabling integrated insight across individuals, teams, and organisations, and supporting better decisions over time.
              </p>
              <a 
                href="/impact-as-plus"
                className="inline-flex items-center gap-2 text-[#2693d0] font-medium text-base hover:gap-3 transition-all duration-300"
              >
                Explore Impact AS Plus™
                <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════
            6) NOT SURE WHERE TO START?
        ═══════════════════════════════════════════════════════════════ */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-2xl">
              <h2 className="text-slate-800 font-bold text-2xl sm:text-3xl lg:text-4xl mb-6">
                Not Sure Where to Start?
              </h2>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-10">
                We work with organisations to identify the most appropriate starting point based on their context, priorities, and constraints.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <a
                  href="/contact"
                  className="inline-flex items-center gap-2 bg-[#2693d0] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#1e7ab8] transition-colors duration-300"
                >
                  Start a conversation
                  <ArrowRight size={18} />
                </a>
                <a
                  href="/impact-as-plus"
                  className="inline-flex items-center gap-2 border-2 border-slate-200 text-slate-700 px-6 py-3 rounded-lg font-medium hover:border-[#2693d0] hover:text-[#2693d0] transition-colors duration-300"
                >
                  Explore Impact AS Plus™
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer hidePreFooterCta={true} />
    </div>
  );
};

export default EngagementPage;
